import React, { useState } from 'react';
import { apiService } from '../services/apiService';
import { Mail, Lock, Eye, EyeOff, ArrowRight } from 'lucide-react';

const LoginPage = ({ onNavigate }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await apiService.login(email, password);
      window.location.reload();
    } catch (err) {
      setError(err.message || 'Invalid credentials. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--c-bg)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 24,
      backgroundImage: `
        radial-gradient(ellipse 70% 60% at 50% -10%, rgba(0,200,150,0.12) 0%, transparent 60%),
        radial-gradient(ellipse 40% 40% at 80% 90%, rgba(141,198,63,0.06) 0%, transparent 50%)
      `,
    }}>
      <div style={{ width: '100%', maxWidth: 420 }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 14, marginBottom: 16 }}>
            <img src="/halvecost-logo.png" alt="WNLeads PRO" style={{ height: 52, width: 52, objectFit: 'contain' }} />
            <div style={{ textAlign: 'left' }}>
              <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 800, fontSize: 28, lineHeight: 1 }}>
                <span style={{ color: 'var(--c-primary)' }}>WNLeads</span>
                <span style={{ color: 'var(--c-accent)' }}> PRO</span>
              </div>
              <div style={{ color: 'var(--c-text-3)', fontSize: 11, letterSpacing: '0.5px', textTransform: 'uppercase', marginTop: 3 }}>
                by WriteNow Agency
              </div>
            </div>
          </div>
          <h1 style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: 22, color: 'var(--c-text)', marginBottom: 6 }}>
            Welcome back
          </h1>
          <p style={{ color: 'var(--c-text-2)', fontSize: 14 }}>
            Sign in to your AI sales workspace
          </p>
        </div>

        {/* Card */}
        <div style={{
          background: 'var(--c-auth-card)',
          border: '1px solid var(--c-border)',
          borderRadius: 20,
          padding: 36,
          backdropFilter: 'blur(20px)',
          boxShadow: 'var(--shadow-elevated)',
        }}>
          {error && (
            <div style={{
              background: 'rgba(239,68,68,0.08)',
              border: '1px solid rgba(239,68,68,0.15)',
              borderRadius: 10, padding: '12px 16px',
              color: 'var(--c-red)', fontSize: 13, marginBottom: 20,
            }}>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            {/* Email */}
            <div>
              <label htmlFor="email" style={{ color: 'var(--c-text-2)', fontSize: 12, fontWeight: 600, letterSpacing: '0.3px', textTransform: 'uppercase', display: 'block', marginBottom: 8 }}>
                Email Address
              </label>
              <div style={{ position: 'relative' }}>
                <Mail size={15} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--c-text-3)' }} />
                <input
                  id="email" type="email" required
                  value={email} onChange={e => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  style={{ width: '100%', paddingLeft: 42, height: 48, borderRadius: 10 }}
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" style={{ color: 'var(--c-text-2)', fontSize: 12, fontWeight: 600, letterSpacing: '0.3px', textTransform: 'uppercase', display: 'block', marginBottom: 8 }}>
                Password
              </label>
              <div style={{ position: 'relative' }}>
                <Lock size={15} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--c-text-3)' }} />
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  required value={password} onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••"
                  style={{ width: '100%', paddingLeft: 42, paddingRight: 44, height: 48, borderRadius: 10 }}
                />
                <button type="button" onClick={() => setShowPassword(s => !s)} style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--c-text-3)', display: 'flex' }}>
                  {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              style={{
                marginTop: 6, height: 50, borderRadius: 12,
                background: 'linear-gradient(135deg, var(--c-primary), var(--c-primary-dim))',
                border: 'none', cursor: loading ? 'not-allowed' : 'pointer',
                color: 'var(--c-btn-text)', fontWeight: 700, fontSize: 15,
                fontFamily: "'Inter', sans-serif",
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                opacity: loading ? 0.7 : 1,
                boxShadow: '0 8px 24px var(--c-primary-glow)',
                transition: 'all 0.2s',
              }}
            >
              {loading ? (
                <div className="spinner" style={{ width: 18, height: 18, borderWidth: 2 }} />
              ) : (
                <> Sign In <ArrowRight size={16} /> </>
              )}
            </button>
          </form>
        </div>

        {/* Register link */}
        <p style={{ textAlign: 'center', marginTop: 20, color: 'var(--c-text-2)', fontSize: 14 }}>
          Don't have an account?{' '}
          <button onClick={onNavigate} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--c-primary)', fontWeight: 600 }}>
            Create account
          </button>
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
